import React, { useRef, useState } from "react";

import { Play, Download } from "lucide-react";

const formatarDuracao = (segundos) => {
	if (!Number.isFinite(segundos)) return null;

	const total = Math.round(segundos);
	const minutos = Math.floor(total / 60);
	const resto = total % 60;

	return `${minutos}:${String(resto).padStart(2, "0")}`;
};

// Vídeo na conversa, como o WhatsApp mostra: um quadro do próprio vídeo com o
// play no meio e a duração no canto. Antes era um <video controls> cru — um
// retângulo preto do qual não dava para saber nem se o vídeo tinha dois
// segundos ou dez minutos antes de apertar play.
const VideoNaConversa = ({ url }) => {
	const videoRef = useRef(null);
	const [duracao, setDuracao] = useState(null);
	const [tocando, setTocando] = useState(false);
	const [erro, setErro] = useState(false);

	const tocar = () => {
		setTocando(true);
		// O play precisa vir do clique da pessoa: iniciado sozinho, o
		// navegador bloqueia.
		videoRef.current?.play();
	};

	if (erro) {
		return (
			<div className="flex h-[200px] w-[250px] items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
				Não foi possível carregar o vídeo
			</div>
		);
	}

	return (
		<div className="relative mb-1 w-[250px]">
			<video
				ref={videoRef}
				// O fragmento `#t=0.1` faz o navegador desenhar o quadro desse
				// instante como capa. Sem ele o vídeo aparece como um
				// retângulo preto até alguém dar play.
				src={`${url}#t=0.1`}
				preload="metadata"
				controls={tocando}
				playsInline
				onLoadedMetadata={(e) => {
					const el = e.currentTarget;
					setDuracao(el.duration);

					// O fragmento na URL nem sempre basta: o Chrome carrega os
					// metadados e deixa o quadro preto até alguém dar play. Pedir
					// o instante explicitamente força o seek, e é o seek que
					// desenha a capa. Baixa só o pedaço do arquivo, não o vídeo
					// inteiro.
					if (!tocando && el.currentTime === 0) {
						try {
							el.currentTime = 0.1;
						} catch {
							// Formato que não aceita seek antes de carregar mais:
							// fica sem capa, o play continua funcionando.
						}
					}
				}}
				onPlay={() => setTocando(true)}
				onError={() => setErro(true)}
				className="w-full max-h-[70vh] rounded-lg bg-black object-contain"
			/>

			{!tocando && (
				<button
					type="button"
					onClick={tocar}
					aria-label="Reproduzir vídeo"
					className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/20 transition hover:bg-black/30"
				>
					<span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/60 text-white">
						<Play className="ml-0.5 h-6 w-6 fill-current" />
					</span>
				</button>
			)}

			{!tocando && duracao !== null && (
				<span className="pointer-events-none absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1.5 py-0.5 text-[11px] font-medium text-white">
					{formatarDuracao(duracao)}
				</span>
			)}

			<a
				href={url}
				target="_blank"
				rel="noopener noreferrer"
				title="Baixar vídeo"
				aria-label="Baixar vídeo"
				className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-90 hover:bg-black/80"
			>
				<Download className="h-3.5 w-3.5" />
			</a>
		</div>
	);
};

export default VideoNaConversa;
